import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@/components/Spinner/Spinner";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { useGetPipedriveSyncLogsQuery } from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { PipedriveEntityType, PipedriveSyncLog } from "../schema/pipedrive";

import { firstSyncLogError } from "./hubspotSyncLog.utils";

const LIMIT = 20;

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);

	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const statusColors: Record<string, string> = {
	SUCCESS: "bg-green-100 text-green-800",
	PARTIAL: "bg-yellow-100 text-yellow-800",
	FAILED: "bg-red-100 text-red-800",
	PENDING: "bg-gray-100 text-gray-800",
	IN_PROGRESS: "bg-blue-100 text-blue-800",
};

const StatusBadge: FunctionComponent<{ status: string }> = ({ status }) => (
	<span
		className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[status] ?? "bg-gray-100 text-gray-800"}`}
	>
		{status}
	</span>
);

export const PipedriveSyncLogsTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.syncLogs.${key}`);

	const [entityFilter, setEntityFilter] = useState<string | undefined>(undefined);
	const [offset, setOffset] = useState(0);

	const { data: logs, isLoading } = useGetPipedriveSyncLogsQuery({
		entityType: entityFilter,
		limit: LIMIT,
		offset,
	});

	const entityOptions: DropdownAutoCompleteItem[] = useMemo(
		() => [
			{ id: "ALL", title: ts("allEntities") },
			{ id: PipedriveEntityType.LEAD, title: "Lead" },
			{ id: PipedriveEntityType.PERSON, title: "Person" },
			{ id: PipedriveEntityType.ORGANIZATION, title: "Organization" },
			{ id: PipedriveEntityType.DEAL, title: "Deal" },
		],
		[ts]
	);

	const handleEntityFilterChange = (value: DropdownAutoCompleteItem) => {
		setEntityFilter(value.id === "ALL" ? undefined : (value.id as string));
		setOffset(0);
	};

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Spinner />
			</div>
		);
	}

	const extractItems = (raw: unknown): PipedriveSyncLog[] => {
		if (Array.isArray(raw)) {
			return raw as PipedriveSyncLog[];
		}

		if (raw && typeof raw === "object") {
			const candidate = raw as Record<string, unknown>;

			for (const key of ["data", "logs", "items", "results"]) {
				if (Array.isArray(candidate[key])) {
					return candidate[key] as PipedriveSyncLog[];
				}
			}
		}

		return [];
	};

	const items: PipedriveSyncLog[] = extractItems(logs);

	return (
		<div className="flex flex-col w-full gap-4">
			<div className="text-sm text-text-meta">{ts("description")}</div>

			<div className="w-full max-w-[250px]">
				<Dropdown
					data={entityOptions}
					defaultValue={entityOptions[0]}
					floating={true}
					handleSelect={handleEntityFilterChange}
					label={ts("filterByEntity")}
				/>
			</div>

			{items.length === 0 ? (
				<div className="text-center py-12 text-text-meta text-sm">{ts("noLogs")}</div>
			) : (
				<div className="rounded-xl border border-border-light bg-brand-white overflow-hidden">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border-light bg-gray-50">
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("entityType")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("status")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("synced")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("failed")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("error")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("startedAt")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("completedAt")}
								</th>
							</tr>
						</thead>
						<tbody>
							{items.map((log) => (
								<tr key={log.id} className="border-b border-border-light last:border-b-0">
									<td className="px-4 py-3 text-text">{log.entityType}</td>
									<td className="px-4 py-3">
										<StatusBadge status={log.status} />
									</td>
									<td className="px-4 py-3 text-text">{log.recordsSynced}</td>
									<td className="px-4 py-3 text-text">{log.recordsFailed}</td>
									<td
										className="px-4 py-3 text-text-meta text-xs max-w-[260px] truncate"
										title={firstSyncLogError(log) ?? undefined}
									>
										{firstSyncLogError(log) ?? "—"}
									</td>
									<td className="px-4 py-3 text-text-meta">{formatDate(log.createdAt)}</td>
									<td className="px-4 py-3 text-text-meta">
										{log.completedAt ? formatDate(log.completedAt) : "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{items.length >= LIMIT && (
				<div className="flex justify-center gap-2 mt-2">
					<button
						className="px-3 py-1 text-sm rounded border border-border-light disabled:opacity-50"
						disabled={offset === 0}
						type="button"
						onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
					>
						{t("basics.previous")}
					</button>
					<button
						className="px-3 py-1 text-sm rounded border border-border-light"
						type="button"
						onClick={() => setOffset((prev) => prev + LIMIT)}
					>
						{t("basics.next")}
					</button>
				</div>
			)}
		</div>
	);
};
